import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormArray } from '@angular/forms';
import { MatDialogRef } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { RestockLotesService } from '../../services/restock-lotes.service';
import { ProductosService } from '../../services/productos.service';

enum TipoProduccion {
  IMPRESION_3D = 'impresion_3d',
  TE = 'te',
  MANUAL = 'manual',
}

@Component({
  selector: 'app-restock-dialog',
  templateUrl: './restock-dialog.component.html',
  styleUrls: ['./restock-dialog.component.scss']
})
export class RestockDialogComponent implements OnInit {
  TipoProduccion = TipoProduccion;
  isLoading = false;

  paso1Form: FormGroup;
  paso2Form: FormGroup;
  itemsForm: FormGroup;

  productosResultados: any[] = [];
  busquedaProducto = '';
  buscandoProducto = false;

  constructor(
    private fb: FormBuilder,
    private dialogRef: MatDialogRef<RestockDialogComponent>,
    private restockLotesService: RestockLotesService,
    private productosService: ProductosService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.paso1Form = this.fb.group({
      tipoProduccion: [TipoProduccion.IMPRESION_3D, Validators.required],
      observaciones: ['']
    });

    this.paso2Form = this.fb.group({
      materialKg: [null],
      costoPorKg: [null],
      kilosComprados: [null],
      costoPorKilo: [null]
    });

    this.itemsForm = this.fb.group({
      items: this.fb.array([])
    });
  }

  get tipoProduccion(): TipoProduccion {
    return this.paso1Form.get('tipoProduccion')?.value;
  }

  get items(): FormArray {
    return this.itemsForm.get('items') as FormArray;
  }

  get costoTotalMaterial(): number {
    if (this.tipoProduccion === TipoProduccion.IMPRESION_3D) {
      const kg = Number(this.paso2Form.get('materialKg')?.value) || 0;
      const costo = Number(this.paso2Form.get('costoPorKg')?.value) || 0;
      return kg * costo;
    }
    if (this.tipoProduccion === TipoProduccion.TE) {
      const kg = Number(this.paso2Form.get('kilosComprados')?.value) || 0;
      const costo = Number(this.paso2Form.get('costoPorKilo')?.value) || 0;
      return kg * costo;
    }
    return 0;
  }

  get totalUnidades(): number {
    return this.items.controls.reduce((sum, item) => {
      if (this.tipoProduccion === TipoProduccion.IMPRESION_3D) {
        return sum + (Number(item.get('unidades')?.value) || 0);
      }
      return sum + (Number(item.get('cantidad')?.value) || 0);
    }, 0);
  }

  get costoUnitario(): number {
    return this.totalUnidades > 0 ? this.costoTotalMaterial / this.totalUnidades : 0;
  }

  get gananciaPotencial(): number {
    return this.items.controls.reduce((sum, item) => {
      const precio = Number(item.get('precioVenta')?.value) || 0;
      const unidades = this.tipoProduccion === TipoProduccion.IMPRESION_3D
        ? (Number(item.get('unidades')?.value) || 0)
        : (Number(item.get('cantidad')?.value) || 0);
      return sum + precio * unidades;
    }, 0);
  }

  buscarProductos(termino: string): void {
    if (!termino || termino.length < 2) {
      this.productosResultados = [];
      return;
    }
    this.buscandoProducto = true;
    this.productosService.buscarProductos(termino).subscribe({
      next: (productos) => {
        this.productosResultados = productos || [];
        this.buscandoProducto = false;
      },
      error: () => {
        this.buscandoProducto = false;
        this.productosResultados = [];
      }
    });
  }

  agregarItem(producto?: any): void {
    const itemGroup = this.fb.group({
      productoId: [producto?.id || null, Validators.required],
      productoNombre: [producto?.nombre || ''],
      unidades: [null],
      unidadesDanadas: [0],
      gramos: [null],
      cantidad: [null],
      precioVenta: [producto?.precio || null, [Validators.required, Validators.min(0.01)]]
    });
    this.items.push(itemGroup);
    this.busquedaProducto = '';
    this.productosResultados = [];
  }

  eliminarItem(index: number): void {
    this.items.removeAt(index);
  }

  confirmar(): void {
    if (this.isLoading) return;
    if (this.items.length === 0) {
      this.toastr.warning('Agrega al menos un producto');
      return;
    }

    const dto: any = {
      tipoProduccion: this.tipoProduccion,
      observaciones: this.paso1Form.get('observaciones')?.value || undefined,
      items: this.items.controls.map(item => ({
        productoId: item.get('productoId')?.value,
        unidades: item.get('unidades')?.value,
        unidadesDanadas: item.get('unidadesDanadas')?.value || 0,
        gramos: item.get('gramos')?.value,
        cantidad: item.get('cantidad')?.value,
        precioVenta: Number(item.get('precioVenta')?.value)
      }))
    };

    if (this.tipoProduccion === TipoProduccion.IMPRESION_3D) {
      dto.materialKg = Number(this.paso2Form.get('materialKg')?.value);
      dto.costoPorKg = Number(this.paso2Form.get('costoPorKg')?.value);
    } else if (this.tipoProduccion === TipoProduccion.TE) {
      dto.kilosComprados = Number(this.paso2Form.get('kilosComprados')?.value);
      dto.costoPorKilo = Number(this.paso2Form.get('costoPorKilo')?.value);
    }

    this.isLoading = true;
    this.restockLotesService.crearConProduccion(dto).subscribe({
      next: () => {
        this.toastr.success('Producción registrada exitosamente');
        this.dialogRef.close(true);
      },
      error: (err) => {
        const msg = err?.error?.message || 'Error al registrar la producción';
        this.toastr.error(msg);
        this.isLoading = false;
      }
    });
  }

  cancelar(): void {
    this.dialogRef.close(false);
  }
}
